import { CloseIcon } from '@fourthwall/icons'
import React, { useEffect } from 'react'
import { RemoveScroll } from 'react-remove-scroll'
import { ButtonGroup } from '../../common/ButtonGroup'
import { Heading } from '../../common/Heading'
import { Icon } from '../../common/Icon'
import { useFocus } from '../../hooks/useFocus'
import { useMediaQueryContext } from '../../providers/MediaQueryProvider'
import { Skeleton } from '../Skeleton'
import * as S from './ModalBox.styled'
import type { Props } from './types'

export const ModalBox = ({
  title,
  size = 'medium',
  maxWidth,
  width,
  withoutPadding = false,
  withoutCloseButton = false,
  disableScrolling = false,
  blurOverlay = false,
  topContent,
  fullWidth,
  footer,
  buttons,
  buttonsDirection,
  loading = false,
  children,
  onClose,
}: Props) => {
  const media = useMediaQueryContext()
  const [closeButtonRef, setCloseButtonFocus] = useFocus()

  useEffect(() => {
    setCloseButtonFocus()
  }, []) // make sure it's an empty array

  if (loading) {
    return (
      <RemoveScroll>
        <S.Container $blurOverlay={blurOverlay}>
          <S.Overlay data-testid="ModalBox.Overlay" onClick={onClose} />
          <ModalBoxSkeleton
            {...{ size, maxWidth, width, title, buttons, footer, withoutPadding }}
          />
        </S.Container>
      </RemoveScroll>
    )
  }

  return (
    <RemoveScroll>
      <S.Container $blurOverlay={blurOverlay}>
        <S.Overlay data-testid="ModalBox.Overlay" onClick={onClose} />
        <S.Box
          size={size}
          maxWidth={maxWidth}
          width={width}
          $withoutPadding={withoutPadding}
          $disableScrolling={disableScrolling}
          role="dialog"
          aria-modal="true"
          data-testid="ModalBox.Content"
          id="modal-box"
        >
          {onClose && !withoutCloseButton && (
            <S.CloseButton aria-label="Close" type="button" onClick={onClose} ref={closeButtonRef}>
              <Icon component={CloseIcon} height={media.tablet ? 16 : 12} />
            </S.CloseButton>
          )}
          {topContent && (
            <S.TopContentContainer $withoutPadding={withoutPadding}>
              {topContent}
            </S.TopContentContainer>
          )}
          {title && (
            <S.TitleContainer>
              <Heading type={media.tablet ? 'h3' : 'h4'} weight="semibold">
                {title}
              </Heading>
            </S.TitleContainer>
          )}

          <S.ChildrenContainer $disableScrolling={disableScrolling}>{children}</S.ChildrenContainer>

          {(buttons || footer) && (
            <S.Footer>
              {footer && <S.FooterContent>{footer}</S.FooterContent>}
              {buttons && (
                <S.ButtonsContent>
                  <ButtonGroup
                    justify="right"
                    align="center"
                    space={media.tablet ? 'large' : 'medium'}
                    fullWidth={fullWidth || !media.tablet}
                    direction={buttonsDirection || media.tablet ? 'row' : 'column-reverse'}
                  >
                    {buttons.map((button, index) =>
                      React.cloneElement(button, { key: index, fullWidth: !media.tablet }),
                    )}
                  </ButtonGroup>
                </S.ButtonsContent>
              )}
            </S.Footer>
          )}
        </S.Box>
      </S.Container>
    </RemoveScroll>
  )
}

const ModalBoxSkeleton = ({
  size = 'medium',
  maxWidth,
  width,
  title,
  buttons,
  footer,
  withoutPadding,
}: Pick<
  Props,
  'size' | 'maxWidth' | 'width' | 'topContent' | 'title' | 'buttons' | 'footer' | 'withoutPadding'
>) => {
  return (
    <S.Box
      size={size}
      maxWidth={maxWidth}
      $withoutPadding={withoutPadding}
      width={width}
      role="dialog"
      aria-modal="true"
      data-testid="ModalBox.Content"
      $disableScrolling
      id="modal-box"
    >
      {title && (
        <S.TitleContainer>
          <Skeleton gap={12}>
            <Skeleton.Shape width="100%" height="25px" />
            <Skeleton.Shape width="100%" height="25px" />
          </Skeleton>
        </S.TitleContainer>
      )}
      <S.ChildrenContainer $disableScrolling>
        <Skeleton gap={16}>
          <Skeleton.Shape width="0" height="20px" />
          <Skeleton.Shape width="80%" height="20px" />
          <Skeleton.Shape width="70%" height="20px" />
          <Skeleton.Shape width="100%" height="20px" />
        </Skeleton>
      </S.ChildrenContainer>

      {(buttons || footer) && (
        <S.Footer>
          <S.FooterContent />
        </S.Footer>
      )}
    </S.Box>
  )
}
